/* Minimal zero-dependency router — a drop-in subset of react-router-dom's API
   (BrowserRouter, Routes, Route, Link, NavLink, useLocation, useNavigate,
   useParams, useSearchParams). Keeps the app dependency-free. */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const RouterCtx = createContext(null);
const ParamsCtx = createContext({});

export function BrowserRouter({ children }) {
  const [loc, setLoc] = useState(() => ({
    pathname: window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    const onPop = () =>
      setLoc({ pathname: window.location.pathname, search: window.location.search });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to, opts = {}) => {
    const url = new URL(to, window.location.origin);
    const next = url.pathname + url.search;
    if (opts.replace) window.history.replaceState({}, "", next);
    else window.history.pushState({}, "", next);
    setLoc({ pathname: url.pathname, search: url.search });
  }, []);

  return <RouterCtx.Provider value={{ loc, navigate }}>{children}</RouterCtx.Provider>;
}

export function useLocation() {
  return useContext(RouterCtx).loc;
}

export function useNavigate() {
  return useContext(RouterCtx).navigate;
}

export function useParams() {
  return useContext(ParamsCtx);
}

export function useSearchParams() {
  const { loc, navigate } = useContext(RouterCtx);
  const params = new URLSearchParams(loc.search);
  const setSearchParams = (next, opts = {}) => {
    const s = next.toString();
    navigate(loc.pathname + (s ? `?${s}` : ""), opts);
  };
  return [params, setSearchParams];
}

function matchPath(pattern, pathname) {
  if (pattern === "*") return { params: {} };
  if (pattern === "/") return pathname === "/" ? { params: {} } : null;
  const pp = pattern.split("/").filter(Boolean);
  const up = pathname.split("/").filter(Boolean);
  if (pp.length !== up.length) return null;
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) params[pp[i].slice(1)] = decodeURIComponent(up[i]);
    else if (pp[i] !== up[i]) return null;
  }
  return { params };
}

export function Routes({ children }) {
  const { pathname } = useLocation();
  const routes = React.Children.toArray(children);
  let matchEl = null;
  let params = {};
  let wildcard = null;

  for (const r of routes) {
    const p = r.props.path;
    if (p === "*") { wildcard = r; continue; }
    const m = matchPath(p, pathname);
    if (m) { matchEl = r; params = m.params; break; }
  }
  if (!matchEl && wildcard) matchEl = wildcard;
  if (!matchEl) return null;

  return <ParamsCtx.Provider value={params}>{matchEl.props.element}</ParamsCtx.Provider>;
}

export function Route() {
  return null;
}

export function Link({ to, children, onClick, ...rest }) {
  const navigate = useNavigate();
  const handle = (e) => {
    if (onClick) onClick(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={handle} {...rest}>
      {children}
    </a>
  );
}

export function NavLink({ to, children, style, className, end, ...rest }) {
  const { pathname } = useLocation();
  const isActive =
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/");
  const resolvedStyle = typeof style === "function" ? style({ isActive }) : style;
  const resolvedClass = typeof className === "function" ? className({ isActive }) : className;
  return (
    <Link to={to} style={resolvedStyle} className={resolvedClass} {...rest}>
      {children}
    </Link>
  );
}
